
import { supabase } from "@/integrations/supabase/client";

export interface ConnectionTestResult {
  success: boolean;
  error?: string;
  latency?: number;
  details?: {
    authReachable: boolean;
    databaseReachable: boolean;
    timestamp: string;
  };
}

export const testSupabaseConnection = async (): Promise<ConnectionTestResult> => {
  const startTime = Date.now();
  console.log("=== SUPABASE CONNECTION TEST ===");
  console.log("Testing Supabase connection...");
  
  try {
    // Test 1: Check if we can reach the auth service
    console.log("Testing auth service...");
    const { data: session, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error("Auth service error:", sessionError);
      return {
        success: false,
        error: `Auth service error: ${sessionError.message}`,
        details: {
          authReachable: false,
          databaseReachable: false,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    console.log("Auth service reachable. Session:", session ? "exists" : "none");
    
    // Test 2: Check if we can reach the database
    console.log("Testing database connection...");
    const { data, error: dbError } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
    
    if (dbError) {
      console.error("Database error:", dbError);
      return {
        success: false,
        error: `Database error: ${dbError.message}`,
        details: {
          authReachable: true,
          databaseReachable: false,
          timestamp: new Date().toISOString()
        }
      };
    }
    
    const latency = Date.now() - startTime;
    console.log(`Database reachable. Connection test passed in ${latency}ms`);
    
    return {
      success: true,
      latency,
      details: {
        authReachable: true,
        databaseReachable: true,
        timestamp: new Date().toISOString()
      }
    };
    
  } catch (error) {
    const latency = Date.now() - startTime;
    console.error("Connection test failed:", error);
    
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown connection error",
      latency,
      details: {
        authReachable: false,
        databaseReachable: false,
        timestamp: new Date().toISOString()
      }
    };
  }
};

export const diagnoseConnectionIssue = (result: ConnectionTestResult): string => {
  if (result.success) {
    return "Connection is healthy";
  }
  
  if (!result.details?.authReachable) {
    return "Cannot reach Supabase auth service. Check your internet connection and Supabase URL.";
  }
  
  if (!result.details?.databaseReachable) {
    return "Auth service reachable but database connection failed. This might be a temporary Supabase issue.";
  }
  
  return result.error || "Unknown connection issue";
};
